/**
 * Upload helper — wraps axios with real upload progress and bounded parallelism.
 *
 * Why this exists: phone photos on cellular take 10-30s to upload. Without a
 * progress signal, users perceive the spinner as "frozen". This helper exposes
 * per-file progress via `onProgress(percent)` for any multipart endpoint and
 * lets us run several uploads in parallel without thread-blocking.
 */
import { api } from "./api";

/**
 * Upload a single file via multipart with progress.
 * Returns the parsed response data.
 *
 * @param {string} url         endpoint path (will be prefixed with /api)
 * @param {File|Blob} file     the file to upload
 * @param {object} opts
 * @param {string} opts.fieldName     form field name (default "file")
 * @param {object} opts.extraFields   extra string fields to append
 * @param {function} opts.onProgress  callback(pct 0-100) fired while uploading
 * @param {function} opts.onDone      callback(response_data) when complete
 */
export async function uploadWithProgress(url, file, opts = {}) {
  const {
    fieldName = "file",
    extraFields = {},
    onProgress,
  } = opts;
  const fd = new FormData();
  fd.append(fieldName, file, file.name || "upload.bin");
  for (const [k, v] of Object.entries(extraFields)) {
    if (v !== undefined && v !== null) fd.append(k, String(v));
  }
  const r = await api.post(url, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    // axios fires this for the request body (upload), not the response body.
    onUploadProgress: (evt) => {
      if (!onProgress) return;
      // evt.total may be undefined for streams without Content-Length.
      const pct = evt.total
        ? Math.min(100, Math.round((evt.loaded * 100) / evt.total))
        : 0;
      onProgress(pct);
    },
    // Defensive timeout: 8 min (DICOM CBCT exports can take a while).
    timeout: 8 * 60 * 1000,
  });
  return r.data;
}

/**
 * Upload many files with bounded concurrency. Each file gets its own
 * progress callback (the upper layer can render a row per file).
 *
 * @param {string} url
 * @param {Array<{id: string, file: File|Blob, extraFields?: object}>} items
 * @param {object} opts
 * @param {number} opts.concurrency  default 3 (good for mobile)
 * @param {string} opts.fieldName    default "file"
 * @param {function} opts.onItemProgress  (id, pct) per-file
 * @param {function} opts.onItemSuccess   (id, responseData)
 * @param {function} opts.onItemError     (id, errorMessage)
 *
 * Resolves when ALL items have completed (success or failure).
 */
export async function uploadManyWithProgress(url, items, opts = {}) {
  const {
    concurrency = 3,
    fieldName = "file",
    onItemProgress,
    onItemSuccess,
    onItemError,
  } = opts;

  const queue = [...items];
  const results = new Map();

  const worker = async () => {
    while (queue.length) {
      const item = queue.shift();
      if (!item) break;
      try {
        const data = await uploadWithProgress(url, item.file, {
          fieldName,
          extraFields: item.extraFields || {},
          onProgress: (pct) => onItemProgress && onItemProgress(item.id, pct),
        });
        results.set(item.id, { ok: true, data });
        if (onItemSuccess) onItemSuccess(item.id, data);
      } catch (err) {
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          "Upload failed";
        results.set(item.id, { ok: false, error: msg });
        if (onItemError) onItemError(item.id, msg);
      }
    }
  };

  const workers = Array.from(
    { length: Math.max(1, Math.min(concurrency, items.length || 1)) },
    () => worker()
  );
  await Promise.all(workers);
  return results;
}

/**
 * Cooperative-yield helper used to keep the main thread responsive between
 * synchronous CPU steps (e.g. canvas-based photo pre-flight). Falls back to
 * setTimeout where requestIdleCallback isn't supported (Safari mobile).
 */
export function yieldToBrowser() {
  return new Promise((resolve) => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(() => resolve(), { timeout: 50 });
    } else {
      setTimeout(resolve, 0);
    }
  });
}
